import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import {
  TermsAndConditions,
  TermsAndConditionsStateTypes,
} from "./TermsAndConditions.util";

const name = "terms-and-conditions";
const extraActions = createExtraActions();

const TermsAndConditionsInitialState: TermsAndConditionsStateTypes = {
  createTermsAndConditions: ResetApiState(null),
  resetCreateTermsAndConditions: ResetApiState(null),

  termsAndConditions: ResetApiState(null),
  resetTermsAndConditions: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createTermsAndConditions = createAsyncThunk<
    ResponseWrapper<TermsAndConditions>,
    any
  >(`/create-${name}`, async (data: any, thunkApi) =>
    axios
      .post(`/${name}`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateTermsAndConditions = createAction(`/reset-create-${name}`);

  const getTermsAndConditions = createAsyncThunk<TermsAndConditions, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetTermsAndConditions = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createTermsAndConditions,
    resetCreateTermsAndConditions,
    getTermsAndConditions,
    resetTermsAndConditions,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<TermsAndConditionsStateTypes>
) {
  return {
    ...createTermsAndConditionsReducer(),
    ...resetCreateTermsAndConditions(),
    ...termsAndConditionsReducer(),
    ...resetTermsAndConditions(),
    ...resetStatesReducer(),
  };

  function createTermsAndConditionsReducer() {
    return {
      ...builder.addCase(
        extraActions.createTermsAndConditions.pending,
        (state, action) => {
          state.createTermsAndConditions = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createTermsAndConditions.fulfilled,
        (state, action) => {
          state.createTermsAndConditions = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createTermsAndConditions.rejected,
        (state, action) => {
          state.createTermsAndConditions = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateTermsAndConditions() {
    return {
      ...builder.addCase(
        extraActions.resetCreateTermsAndConditions,
        (state, action) => {
          state.resetCreateTermsAndConditions = ResetApiState(true);
          state.createTermsAndConditions = ResetApiState(null);
        }
      ),
    };
  }

  function termsAndConditionsReducer() {
    return {
      ...builder.addCase(
        extraActions.getTermsAndConditions.pending,
        (state, action) => {
          state.termsAndConditions = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTermsAndConditions.fulfilled,
        (state, action) => {
          state.termsAndConditions = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTermsAndConditions.rejected,
        (state, action) => {
          state.termsAndConditions = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetTermsAndConditions() {
    return {
      ...builder.addCase(
        extraActions.resetTermsAndConditions,
        (state, action) => {
          state.resetTermsAndConditions = ResetApiState(true);
          state.termsAndConditions = ResetApiState(null);
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createTermsAndConditions = ResetApiState(null);
        state.resetCreateTermsAndConditions = ResetApiState(null);
        state.termsAndConditions = ResetApiState(null);
        state.resetTermsAndConditions = ResetApiState(null);
        state.resetTermsAndConditions = ResetApiState(null);
      }),
    };
  }
}

const TermsAndConditionsSlice = createSlice({
  name,
  initialState: TermsAndConditionsInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const TermsAndConditionsActions = {
  ...TermsAndConditionsSlice.actions,
  ...extraActions,
};
export const TermsAndConditionsReducer = TermsAndConditionsSlice.reducer;
